<template>
    <div>
        <div v-if="logLoading" class="text-center py-5 ix-text-secondary">
            <i class="far fa-circle-notch fa-spin fa-lg"></i>
        </div>

        <template v-else-if="inboxLog">

            <div class="bill-inbox-section-divider">
                {{ isCreditNote ? 'Avoir fournisseur importé' : 'Facture fournisseur importée' }}
            </div>

            <!-- Section Facture / Avoir prod-up -->
            <div class="bill-inbox-section">
                <div class="bill-inbox-section-title d-flex align-items-center justify-content-between" style="margin-bottom: 12px;">
                    <span>{{ isCreditNote ? 'Avoir prod-up' : 'Facture prod-up' }}</span>
                    <a v-if="inboxLog.bill" :href="inboxLog.bill.url" target="_blank" class="ix-link" style="font-size: 0.82em; text-transform: none; font-weight: 400; letter-spacing: 0;">
                        {{ inboxLog.bill.number }} <i class="far fa-external-link ml-1"></i>
                    </a>
                    <a v-else-if="inboxLog.credit_supplier" :href="inboxLog.credit_supplier.url" target="_blank" class="ix-link" style="font-size: 0.82em; text-transform: none; font-weight: 400; letter-spacing: 0;">
                        {{ inboxLog.credit_supplier.number }} <i class="far fa-external-link ml-1"></i>
                    </a>
                </div>

                <template v-if="inboxLog.bill">
                    <div class="form-group mb-2">
                        <div class="ix-text-small ix-text-secondary mb-1">Fournisseur</div>
                        <a v-if="inboxLog.bill.company_url" :href="inboxLog.bill.company_url" class="ix-link ix-text-bold" target="_blank">
                            {{ inboxLog.bill.company || '-' }}
                        </a>
                        <div v-else class="ix-text-bold">{{ inboxLog.bill.company || '-' }}</div>
                        <span v-if="hasDiff(inboxLog.bill.company, sourceSupplierName)"
                              class="bill-inbox-diff-badge"
                              :title="'Source : ' + (sourceSupplierName || '-')">≠ source</span>
                    </div>
                    <div v-if="inboxLog.bill.address" class="form-group mb-2">
                        <div class="ix-text-small ix-text-secondary mb-1">Adresse</div>
                        <div>{{ inboxLog.bill.address }}</div>
                    </div>
                    <div class="row">
                        <div class="col-6">
                            <div class="form-group mb-2">
                                <div class="ix-text-small ix-text-secondary mb-1">Référence</div>
                                <div>{{ inboxLog.bill.reference || '-' }}
                                    <span v-if="hasDiff(inboxLog.bill.reference, item.data.invoice_number)"
                                          class="bill-inbox-diff-badge" :title="'Source : ' + (item.data.invoice_number || '-')">≠ source</span>
                                </div>
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="form-group mb-2">
                                <div class="ix-text-small ix-text-secondary mb-1">Désignation</div>
                                <div>{{ truncate(inboxLog.bill.designation, 40) || '-' }}
                                    <span v-if="hasDiff(inboxLog.bill.designation, item.data.label)"
                                          class="bill-inbox-diff-badge" :title="'Source : ' + truncate(item.data.label, 40)">≠ source</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-6">
                            <div class="form-group mb-2">
                                <div class="ix-text-small ix-text-secondary mb-1">Date d'émission</div>
                                <div>{{ inboxLog.bill.issued_at || '-' }}
                                    <span v-if="hasDiffDate(inboxLog.bill.issued_at, item.data.date)"
                                          class="bill-inbox-diff-badge" :title="'Source : ' + (item.data.date || '-')">≠ source</span>
                                </div>
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="form-group mb-2">
                                <div class="ix-text-small ix-text-secondary mb-1">Date d'échéance</div>
                                <div>{{ inboxLog.bill.due_at || '-' }}
                                    <span v-if="hasDiffDate(inboxLog.bill.due_at, item.data.deadline)"
                                          class="bill-inbox-diff-badge" :title="'Source : ' + (item.data.deadline || '-')">≠ source</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-4">
                            <div class="form-group mb-2">
                                <div class="ix-text-small ix-text-secondary mb-1">Total HT</div>
                                <div>{{ fmtAmt(inboxLog.bill.total_ht) }}
                                    <span v-if="hasDiffAmt(inboxLog.bill.total_ht, item.data.currency_amount_before_tax)"
                                          class="bill-inbox-diff-badge" :title="'Source : ' + fmtAmt(item.data.currency_amount_before_tax)">≠ source</span>
                                </div>
                            </div>
                        </div>
                        <div class="col-4">
                            <div class="form-group mb-2">
                                <div class="ix-text-small ix-text-secondary mb-1">TVA</div>
                                <div>{{ fmtAmt(inboxLog.bill.total_vat) }}
                                    <span v-if="hasDiffAmt(inboxLog.bill.total_vat, item.data.currency_tax)"
                                          class="bill-inbox-diff-badge" :title="'Source : ' + fmtAmt(item.data.currency_tax)">≠ source</span>
                                </div>
                            </div>
                        </div>
                        <div class="col-4">
                            <div class="form-group mb-2">
                                <div class="ix-text-small ix-text-secondary mb-1">Total TTC</div>
                                <div class="ix-text-bold">{{ fmtAmt(inboxLog.bill.total_ttc) }}
                                    <span v-if="hasDiffAmt(inboxLog.bill.total_ttc, item.data.amount)"
                                          class="bill-inbox-diff-badge" :title="'Source : ' + fmtAmt(item.data.amount)">≠ source</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <template v-else-if="inboxLog.credit_supplier">
                    <div class="form-group mb-2">
                        <div class="ix-text-small ix-text-secondary mb-1">Fournisseur</div>
                        <div class="ix-text-bold">
                            {{ inboxLog.credit_supplier.company || '-' }}
                            <span v-if="hasDiff(inboxLog.credit_supplier.company, sourceSupplierName)"
                                  class="bill-inbox-diff-badge"
                                  :title="'Source : ' + (sourceSupplierName || '-')">≠ source</span>
                        </div>
                    </div>
                    <div v-if="inboxLog.credit_supplier.address" class="form-group mb-2">
                        <div class="ix-text-small ix-text-secondary mb-1">Adresse</div>
                        <div>{{ inboxLog.credit_supplier.address }}</div>
                    </div>
                    <div class="form-group mb-2">
                        <div class="ix-text-small ix-text-secondary mb-1">Désignation</div>
                        <div>{{ inboxLog.credit_supplier.designation || '-' }}</div>
                    </div>
                    <div class="row">
                        <div class="col-4">
                            <div class="form-group mb-2">
                                <div class="ix-text-small ix-text-secondary mb-1">Total HT</div>
                                <div>{{ fmtAmt(inboxLog.credit_supplier.total_ht) }}</div>
                            </div>
                        </div>
                        <div class="col-4">
                            <div class="form-group mb-2">
                                <div class="ix-text-small ix-text-secondary mb-1">TVA</div>
                                <div>{{ fmtAmt(inboxLog.credit_supplier.total_vat) }}</div>
                            </div>
                        </div>
                        <div class="col-4">
                            <div class="form-group mb-2">
                                <div class="ix-text-small ix-text-secondary mb-1">Total TTC</div>
                                <div class="ix-text-bold ix-text-danger">{{ fmtAmt(inboxLog.credit_supplier.total_ttc) }}</div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>

            <!-- Section Données source (collapsible) -->
            <div class="bill-inbox-section">
                <div class="d-flex align-items-center justify-content-between" style="cursor: pointer;" @click="sourceExpanded = !sourceExpanded">
                    <div class="bill-inbox-section-title mb-0">
                        Données source
                        <span class="ix-text-secondary" style="text-transform: none; font-weight: 400; letter-spacing: 0;">· {{ item.source }}</span>
                    </div>
                    <i class="far ix-text-secondary" :class="sourceExpanded ? 'fa-chevron-up' : 'fa-chevron-down'" style="font-size: 0.8em;"></i>
                </div>
                <div v-if="sourceExpanded" class="mt-3">
                    <div class="row" style="font-size: 0.9em;">
                        <div class="col-6 mb-2">
                            <div class="ix-text-secondary ix-text-small">Fournisseur</div>
                            <div>{{ item.data.company_name || item.data.supplier_name || '-' }}</div>
                        </div>
                        <div class="col-6 mb-2">
                            <div class="ix-text-secondary ix-text-small">Référence</div>
                            <div>{{ item.data.invoice_number || '-' }}</div>
                        </div>
                        <div class="col-12 mb-2">
                            <div class="ix-text-secondary ix-text-small">Désignation</div>
                            <div>{{ item.data.label || '-' }}</div>
                        </div>
                        <div class="col-6 mb-2">
                            <div class="ix-text-secondary ix-text-small">Date d'émission</div>
                            <div>{{ item.data.date || '-' }}</div>
                        </div>
                        <div class="col-6 mb-2">
                            <div class="ix-text-secondary ix-text-small">Date d'échéance</div>
                            <div>{{ item.data.deadline || '-' }}</div>
                        </div>
                        <div class="col-4 mb-2">
                            <div class="ix-text-secondary ix-text-small">Total HT</div>
                            <div>{{ fmtAmt(item.data.currency_amount_before_tax) }}</div>
                        </div>
                        <div class="col-4 mb-2">
                            <div class="ix-text-secondary ix-text-small">TVA</div>
                            <div>{{ fmtAmt(item.data.currency_tax) }}</div>
                        </div>
                        <div class="col-4 mb-2">
                            <div class="ix-text-secondary ix-text-small">Total TTC</div>
                            <div class="ix-text-bold">{{ fmtAmt(item.data.amount) }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="bill-inbox-section-divider">
                <i class="far fa-link mr-2"></i>
                Rapprochement effectué
            </div>

            <!-- Section Rapprochement effectué -->
            <div class="bill-inbox-section">
                <div class="row">
                    <div class="col-6 mb-2">
                        <div class="ix-text-secondary ix-text-small">Date</div>
                        <div>{{ inboxLog.created_at || '-' }}</div>
                    </div>
                    <div class="col-6 mb-2">
                        <div class="ix-text-secondary ix-text-small">Par</div>
                        <div>{{ inboxLog.user || '-' }}</div>
                    </div>
                    <div v-if="inboxLog.match_status_label" class="col-12 mb-2">
                        <div class="ix-text-secondary ix-text-small">Type</div>
                        <div>{{ inboxLog.match_status_label }}</div>
                    </div>
                    <template v-if="inboxLog.bill && inboxLog.bill.expenses && inboxLog.bill.expenses.length > 0">
                        <div class="col-12 mb-1">
                            <div class="ix-text-secondary ix-text-small">
                                Commande{{ inboxLog.bill.expenses.length > 1 ? 's' : '' }} fournisseur liée{{ inboxLog.bill.expenses.length > 1 ? 's' : '' }}
                            </div>
                        </div>
                        <div v-for="exp in inboxLog.bill.expenses" :key="exp.id" class="col-12 mb-1 d-flex align-items-center" style="gap: 8px;">
                            <a :href="exp.url" target="_blank" class="ix-link">
                                <i class="far fa-file-alt mr-1"></i>{{ exp.number }}
                            </a>
                            <span class="ix-text-secondary" style="font-size: 0.85em;">{{ fmtAmt(exp.amount) }}</span>
                        </div>
                    </template>
                </div>
            </div>

        </template>

        <div v-else-if="item && item.sync_status === 'up_to_date'" class="bill-inbox-section ix-text-secondary ix-text-small py-2">
            Aucun journal de rapprochement trouvé.
        </div>
    </div>
</template>

<script>

import { fmtAmt, truncate, hasDiff, hasDiffDate, hasDiffAmt } from './billInboxUtils.js';

export default {
    name: 'BillInboxReadOnly',

    props: {
        item:               { type: Object, required: true },
        inboxLog:           { type: Object, default: null },
        logLoading:         { type: Boolean, default: false },
        isCreditNote:       { type: Boolean, default: false },
        sourceSupplierName: { type: String, default: '' },
    },

    data() {
        return {
            sourceExpanded: false,
        };
    },

    watch: {
        item() {
            this.sourceExpanded = false;
        },
    },

    methods: {
        fmtAmt,
        truncate,
        hasDiff,
        hasDiffDate,
        hasDiffAmt,
    },
};
</script>

<style scoped>
.bill-inbox-section {
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
}

.bill-inbox-section-title {
    font-size: 0.75em;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6c757d;
    font-weight: 600;
    margin-bottom: 12px;
}

.bill-inbox-diff-badge {
    display: inline-block;
    font-size: 0.72em;
    color: #d97706;
    background: #fffbeb;
    border: 1px solid #fcd34d;
    border-radius: 3px;
    padding: 1px 5px;
    margin-left: 4px;
    cursor: help;
    font-weight: 500;
}

.bill-inbox-section-divider {
    padding: 8px 16px;
    background: #f0f4f8;
    border-top: 2px solid #d0dbe8;
    border-bottom: 2px solid #d0dbe8;
    font-size: 0.72em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #495a6e;
}
</style>
